import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, History, MessageCircle, Trash2, Truck, MapPin, Calendar, FileText, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DispatchHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dispatch-logs`);
      setLogs(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load dispatch history'));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (log) => {
    if (!window.confirm(`Delete this dispatch log to ${log.supplier_name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/dispatch-logs/${log.id}`);
      toast.success('Log deleted');
      fetchLogs();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete'));
    }
  };

  const q = search.toLowerCase();
  const filtered = logs.filter(l => {
    if (!q) return true;
    return (
      l.supplier_name?.toLowerCase().includes(q) ||
      l.customer_ref?.toLowerCase().includes(q) ||
      l.source_number?.toLowerCase().includes(q) ||
      l.cargo?.toLowerCase().includes(q) ||
      l.pickup_location?.toLowerCase().includes(q) ||
      l.dropoff_location?.toLowerCase().includes(q)
    );
  });

  const sourceLabel = (t) => t === 'delivery_order' ? 'DO' : t === 'invoice' ? 'INV' : 'Manual';
  const sourceColor = (t) => t === 'delivery_order' ? 'bg-blue-100 text-blue-700' : t === 'invoice' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700';

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <Link to="/admin/suppliers">
              <Button variant="outline" data-testid="back-btn"><ArrowLeft size={18} /></Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <History size={28} className="text-emerald-600" /> Dispatch History
              </h1>
              <p className="text-gray-600 text-sm mt-1">All jobs sent to suppliers via WhatsApp</p>
            </div>
          </div>
          <Badge variant="outline" className="text-emerald-700 border-emerald-300 px-3 py-1.5">
            <MessageCircle size={14} className="mr-1.5" /> {logs.length} dispatches
          </Badge>
        </div>

        {/* Search */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by supplier, customer, DO/INV no., cargo or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="dispatch-search"
              />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <History size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No dispatches yet</p>
              <p className="text-sm mt-1">Every job you send to a supplier via WhatsApp will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="dispatch-list">
            {filtered.map((log) => (
              <Card key={log.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-gray-900" data-testid={`log-supplier-${log.id}`}>
                          {log.supplier_name}
                        </h3>
                        <Badge className={`text-[10px] ${sourceColor(log.source_type)} hover:${sourceColor(log.source_type)}`}>
                          {sourceLabel(log.source_type)} {log.source_number || ''}
                        </Badge>
                        {log.service && (
                          <Badge variant="outline" className="text-[10px]">
                            <Truck size={10} className="mr-1" /> {log.service}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2">
                        {log.job_date && (
                          <div className="flex items-center gap-1.5">
                            <Calendar size={11} /> {log.job_date}{log.job_time ? ` ${log.job_time}` : ''}
                          </div>
                        )}
                        {log.customer_ref && (
                          <div className="flex items-center gap-1.5 truncate">
                            <FileText size={11} /> {log.customer_ref}
                          </div>
                        )}
                        {log.pickup_location && (
                          <div className="flex items-center gap-1.5 truncate">
                            <MapPin size={11} className="text-emerald-600" /> From: {log.pickup_location}
                          </div>
                        )}
                        {log.dropoff_location && (
                          <div className="flex items-center gap-1.5 truncate">
                            <MapPin size={11} className="text-red-600" /> To: {log.dropoff_location}
                          </div>
                        )}
                        {log.cargo && (
                          <div className="flex items-center gap-1.5 truncate sm:col-span-2">
                            📦 {log.cargo}{log.weight ? ` · ${log.weight}` : ''}
                          </div>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-2">
                        Dispatched: {new Date(log.dispatched_at).toLocaleString('en-SG')}
                        {log.supplier_phone && ` · ${log.supplier_phone}`}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            onClick={() => remove(log)} data-testid={`delete-log-${log.id}`} title="Delete log">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DispatchHistory;
